import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CircleCheck, Info, OctagonX, X } from 'lucide-react';
import { toast as sonnerToast, Toaster as Sonner } from 'sonner';
import { useTheme } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const DEFAULT_DURATION_MS = 4000;

type ToastVariant = 'default' | 'success' | 'error';

type ToastOptions = {
  description?: ReactNode;
  duration?: number;
};

type ToastCardProps = {
  id: string | number;
  title: ReactNode;
  description?: ReactNode;
  variant: ToastVariant;
  duration: number;
};

function ToastCard({
  id,
  title,
  description,
  variant,
  duration,
}: ToastCardProps) {
  const [progress, setProgress] = useState(100);
  const [cycle, setCycle] = useState(0);
  const [paused, setPaused] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);

  const dismiss = useCallback(() => {
    sonnerToast.dismiss(id);
  }, [id]);

  const pauseTimer = useCallback(() => {
    setPaused(true);
  }, []);

  const resumeWithReset = useCallback(() => {
    setProgress(100);
    setPaused(false);
    setCycle((c) => c + 1);
  }, []);

  useEffect(() => {
    if (paused || duration <= 0 || !Number.isFinite(duration)) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const next = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(next);

      if (next <= 0) {
        dismiss();
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [cycle, dismiss, duration, paused]);

  const Icon =
    variant === 'success' ? CircleCheck : variant === 'error' ? OctagonX : Info;

  return (
    <div
      className={cn(
        'relative w-[356px] max-w-[min(356px,calc(100vw-2rem))] overflow-hidden rounded-md border bg-background p-4 pr-10 text-foreground shadow-lg',
        variant === 'error' && 'border-destructive/50'
      )}
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeWithReset}
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="absolute right-2 top-2 rounded-sm p-1 text-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="size-3.5" />
      </button>

      <div className="flex gap-3">
        <Icon
          className={cn(
            'mt-0.5 size-4 shrink-0',
            variant === 'success' && 'text-emerald-600',
            variant === 'error' && 'text-destructive',
            variant === 'default' && 'text-foreground/70'
          )}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-sm font-medium leading-none">{title}</div>
          {description != null && description !== '' ? (
            <div className="text-sm text-muted-foreground">{description}</div>
          ) : null}
        </div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-muted"
      >
        <div
          className={cn(
            'h-full origin-left transition-none',
            variant === 'error' ? 'bg-destructive' : 'bg-primary'
          )}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function showToast(
  variant: ToastVariant,
  title: ReactNode,
  options?: ToastOptions
) {
  const duration = options?.duration ?? DEFAULT_DURATION_MS;

  return sonnerToast.custom(
    (id) => (
      <ToastCard
        id={id}
        title={title}
        description={options?.description}
        variant={variant}
        duration={duration}
      />
    ),
    { duration: Infinity }
  );
}

function toast(title: ReactNode, options?: ToastOptions) {
  return showToast('default', title, options);
}

toast.success = (title: ReactNode, options?: ToastOptions) =>
  showToast('success', title, options);

toast.error = (title: ReactNode, options?: ToastOptions) =>
  showToast('error', title, options);

toast.dismiss = sonnerToast.dismiss;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: 'bg-transparent border-0 shadow-none p-0',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
